import { ChangePasswordBody, ROLE_LABELS } from '@hearth/shared';
import { useMutation } from '@tanstack/react-query';
import { type ChangeEvent, type FormEvent, useState } from 'react';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { api, fieldErrors } from '../../lib/api';
import { EMPTY } from '../../lib/format';

export function AccountPage() {
  const { user } = useAuth();
  if (!user) return null;

  const details: [string, string | null][] = [
    ['Email', user.email],
    ['Title', user.title],
    ['Phone', user.phone],
    ['Timezone', user.timezone],
  ];

  return (
    <Page width="narrow">
      <PageHeader title="Account" description="Your profile and sign-in security." />
      <Card>
        <CardHeader title="Profile" description="Managed by your administrator in Settings → Users." />
        <CardBody className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="flex items-center gap-3 sm:w-56">
            <Avatar name={user.name} size="lg" />
            <div className="min-w-0">
              <div className="truncate font-semibold text-slate-900">{user.name}</div>
              <Badge color="orange" className="mt-1">
                {ROLE_LABELS[user.role]}
              </Badge>
            </div>
          </div>
          <dl className="grid flex-1 grid-cols-1 gap-x-6 gap-y-3 text-[13px] sm:grid-cols-2">
            {details.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-slate-500">{label}</dt>
                <dd className="mt-0.5 truncate text-slate-900">{value || EMPTY}</dd>
              </div>
            ))}
          </dl>
        </CardBody>
      </Card>
      <ChangePasswordCard />
    </Page>
  );
}

type PasswordForm = { currentPassword: string; newPassword: string; confirmPassword: string };
const EMPTY_FORM: PasswordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };

function ChangePasswordCard() {
  const toast = useToast();
  const [form, setForm] = useState<PasswordForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) => api.post<void>('/api/auth/change-password', body),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      toast.success('Password changed');
    },
    onError: (error) => {
      const byField = fieldErrors(error);
      setErrors(byField);
      // Field-level problems show under the inputs; anything else (e.g. wrong current password) on top.
      setFormError(Object.keys(byField).length === 0 ? error.message : null);
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsed = ChangePasswordBody.safeParse({ currentPassword: form.currentPassword, newPassword: form.newPassword });
    const next: Record<string, string> = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        next[path] ??= issue.path[0] === 'currentPassword' ? 'Enter your current password' : issue.message;
      }
    }
    if (form.confirmPassword !== form.newPassword) next.confirmPassword = 'Passwords do not match';
    setErrors(next);
    if (Object.keys(next).length > 0 || !parsed.success) return;
    mutation.mutate(parsed.data);
  };

  const set = (key: keyof PasswordForm) => (e: ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Card>
      <CardHeader title="Change password" description="Use at least 10 characters. Other sessions stay signed in until they expire." />
      <CardBody>
        <form onSubmit={onSubmit} className="flex max-w-sm flex-col gap-4" noValidate>
          {formError && (
            <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
              {formError}
            </p>
          )}
          <Field label="Current password" error={errors.currentPassword}>
            <Input type="password" autoComplete="current-password" value={form.currentPassword} onChange={set('currentPassword')} />
          </Field>
          <Field label="New password" error={errors.newPassword} hint="At least 10 characters.">
            <Input type="password" autoComplete="new-password" value={form.newPassword} onChange={set('newPassword')} />
          </Field>
          <Field label="Confirm new password" error={errors.confirmPassword}>
            <Input type="password" autoComplete="new-password" value={form.confirmPassword} onChange={set('confirmPassword')} />
          </Field>
          <div>
            <Button type="submit" variant="primary" loading={mutation.isPending}>
              Update password
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
